import { useState, useCallback, useMemo } from 'react'
import { Card, Deck } from '../../types'
import { useAppStore } from '../../store'
import { Plus, Minus } from 'lucide-react'

export function DeckEditor() {
  const deckEditor = useAppStore((state) => state.deckEditor)
  const [previewCard, setPreviewCard] = useState<Card | null>(null)
  const [hoveredPosition, setHoveredPosition] = useState({ top: 0, left: 0 })

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

  const getImageUrl = (localImage?: string, fallbackUrl?: string) => {
    if (localImage) {
      return localImage.startsWith('http') ? localImage : `${API_URL}/${localImage}`
    }
    return fallbackUrl
  }

  const deck = deckEditor.currentDeck
  if (!deck) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <p>No hay mazo cargado</p>
      </div>
    )
  }

  const formatConfig = {
    maxCards: 60,
    maxCopies: 3,
  }

  const handleUpdate = useCallback((newCards: typeof deck.cards) => {
    const actions = useAppStore.getState().actions
    actions.setCurrentDeck({ ...deck, cards: newCards })
    actions.setDeckDirty(true)
  }, [deck])

  const handleAddCard = useCallback((card: Card) => {
    let newCards = [...deck.cards]
    const existingIndex = newCards.findIndex((c) => c.cardId === card.id)

    if (existingIndex >= 0) {
      const currentQty = newCards[existingIndex].quantity
      if (currentQty < formatConfig.maxCopies) {
        newCards[existingIndex] = { ...newCards[existingIndex], quantity: currentQty + 1 }
      }
    } else {
      newCards.push({ cardId: card.id, card, quantity: 1 })
    }

    handleUpdate(newCards)
  }, [deck, handleUpdate])

  const handleRemoveCard = useCallback((cardId: string) => {
    let newCards = [...deck.cards]
    const existingIndex = newCards.findIndex((c) => c.cardId === cardId)
    if (existingIndex < 0) return

    const currentQty = newCards[existingIndex].quantity
    if (currentQty <= 1) {
      newCards = newCards.filter((c) => c.cardId !== cardId)
    } else {
      newCards[existingIndex] = { ...newCards[existingIndex], quantity: currentQty - 1 }
    }

    handleUpdate(newCards)
  }, [deck, handleUpdate])

  const handleClearDeck = useCallback(() => {
    handleUpdate([])
  }, [handleUpdate])

  const totalCards = deck.cards.reduce((sum, c) => sum + c.quantity, 0)

  const cardsByType = useMemo(() => {
    const groups: Record<string, typeof deck.cards> = {
      LEADER: [],
      UNIT: [],
      SPELL: [],
      ARTIFACT: [],
    }

    deck.cards.forEach((card) => {
      if (groups[card.card.type]) {
        groups[card.card.type].push(card)
      }
    })

    return groups
  }, [deck.cards])

  const typeLabels: Record<string, { label: string; color: string }> = {
    LEADER: { label: 'Líderes', color: 'text-red-400 border-red-400' },
    UNIT: { label: 'Aliados', color: 'text-blue-400 border-blue-400' },
    SPELL: { label: 'Hechizos', color: 'text-purple-400 border-purple-400' },
    ARTIFACT: { label: 'Oros', color: 'text-yellow-400 border-yellow-400' },
  }

  const handleMouseEnter = (card: Card, e: React.MouseEvent) => {
    setPreviewCard(card)
    const target = e.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    setHoveredPosition({
      top: rect.top,
      left: rect.left + rect.width + 10,
    })
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      <div className="p-3 border-b border-gray-700 bg-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">{deck.name}</h2>
            <span className="text-sm text-gray-400">
              {totalCards}/{formatConfig.maxCards}
            </span>
            {deckEditor.isDirty && <span className="text-yellow-400 text-xs">Sin guardar</span>}
          </div>
          <button
            onClick={handleClearDeck}
            className="px-3 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
          >
            Vaciar
          </button>
        </div>
        <div className="mt-2 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              totalCards === formatConfig.maxCards ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min((totalCards / formatConfig.maxCards) * 100, 100)}%` }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 min-h-0">
        {deck.cards.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-2">No hay cartas en el mazo</p>
              <p className="text-sm">Agrega cartas desde el panel de la izquierda</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(cardsByType).map(([type, cards]) => {
              if (cards.length === 0) return null
              const typeInfo = typeLabels[type]

              return (
                <div key={type}>
                  <div className={`flex items-center gap-2 mb-3 pb-1 border-b ${typeInfo.color}`}>
                    <span className="text-sm font-semibold">{typeInfo.label}</span>
                    <span className="text-xs text-gray-500">
                      {cards.reduce((sum, c) => sum + c.quantity, 0)} cartas
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 pb-4">
                    {cards
                      .sort((a, b) => a.card.name.localeCompare(b.card.name))
                      .map((deckCard) => (
                        <div
                          key={deckCard.cardId}
                          className="relative"
                          onMouseEnter={(e) => handleMouseEnter(deckCard.card, e)}
                          onMouseLeave={() => setPreviewCard(null)}
                        >
                          <div className="relative w-32 group">
                            <div className="relative rounded-xl overflow-hidden bg-gray-800 ring-2 ring-transparent group-hover:ring-blue-500 transition-all duration-200 shadow-xl">
                              <div className="aspect-[2/3]">
                                {(deckCard.card as Card & { localImage?: string }).localImage ? (
                                  <img
                                    src={getImageUrl((deckCard.card as Card & { localImage?: string }).localImage)}
                                    alt={deckCard.card.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : deckCard.card.imageUrl ? (
                                  <img
                                    src={deckCard.card.imageUrl}
                                    alt={deckCard.card.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                    <span className="text-gray-500 text-lg">?</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="absolute top-1.5 right-1.5 bg-black/70 text-white text-xs px-2 py-0.5 rounded font-bold">
                                {deckCard.quantity}
                              </div>

                              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                              
                              <div className="absolute bottom-0 left-0 right-0 p-2">
                                <p className="text-white text-xs truncate font-medium leading-tight">
                                  {deckCard.card.name}
                                </p>
                              </div>
                            </div>

                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 translate-y-2 group-hover:translate-y-0">
                              <button
                                onClick={() => handleRemoveCard(deckCard.cardId)}
                                className="p-2 bg-red-600 text-white rounded-full hover:bg-red-500 shadow-lg hover:scale-110 transition-transform"
                                title="Quitar"
                              >
                                <Minus className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleAddCard(deckCard.card)}
                                disabled={deckCard.quantity >= formatConfig.maxCopies}
                                className="p-2 bg-green-600 text-white rounded-full hover:bg-green-500 shadow-lg hover:scale-110 transition-transform disabled:opacity-50 disabled:hover:scale-100"
                                title="Agregar"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {previewCard && (
        <div
          className="fixed z-50 bg-gray-800 rounded-xl shadow-2xl p-4 border border-gray-600 pointer-events-none"
          style={{
            top: hoveredPosition.top,
            left: hoveredPosition.left,
            maxWidth: '320px',
          }}
        >
          <div className="flex gap-4">
            {(previewCard as Card & { localImage?: string }).localImage ? (
                <img
                  src={getImageUrl((previewCard as Card & { localImage?: string }).localImage)}
                alt={previewCard.name}
                className="w-36 h-50 object-cover rounded-lg shadow-lg flex-shrink-0"
              />
            ) : previewCard.imageUrl && (
              <img
                src={previewCard.imageUrl}
                alt={previewCard.name}
                className="w-36 h-50 object-cover rounded-lg shadow-lg flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-lg leading-tight">{previewCard.name}</h3>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-gray-400 text-sm">{previewCard.faction}</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-300 text-sm">{previewCard.type}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-yellow-600 text-white px-2.5 py-1 rounded text-sm font-bold">
                  {previewCard.cost}
                </span>
                {previewCard.strength !== undefined && (
                  <span className="bg-blue-600 text-white px-2.5 py-1 rounded text-sm font-bold">
                    {previewCard.strength}
                  </span>
                )}
              </div>
              {previewCard.effect && (
                <p className="text-gray-300 text-sm mt-2 leading-relaxed">
                  {typeof previewCard.effect === 'string'
                    ? previewCard.effect
                    : previewCard.effect.description}
                </p>
              )}
              <p className="text-gray-500 text-xs mt-2">{previewCard.edition}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
